import {
  businessToday,
  isContiguous,
  MONTH_ABBR,
  normalizeMonths,
  parseLongDate,
  periodEnd,
  periodLabel,
  periodStart,
  quarterMonths,
  wholeQuarter,
} from '@/lib/period'

import type {
  AccountSetting,
  BudgetOption,
  Department,
  DepartmentSettings,
  DepartmentVia,
  Periods,
  Runway,
  RunwayPeriod,
  SageDepartment,
  TileSetting,
} from './types'

// The SPA and the API deploy through separate pipelines, so for a few minutes after a release the
// new SPA can talk to the previous API (or the reverse). Fields added to the contract after v1 are
// therefore filled with safe defaults here, at the API boundary, so components can rely on the full
// shapes and never crash on a missing field.

type LooseDepartment = Omit<Department, 'grouping' | 'source_depts'> &
  Partial<Pick<Department, 'grouping' | 'source_depts'>>

type LooseRunway = Partial<Omit<Runway, 'departments'>> & { departments: Record<string, LooseDepartment> }

function normalizeDepartment(raw: LooseDepartment): Department {
  return {
    ...raw,
    grouping: raw.grouping ?? (raw.auto ? 'sage' : 'custom'),
    source_depts: raw.source_depts ?? [],
  }
}

export function normalizeRunway(raw: LooseRunway): Runway {
  const departments: Record<string, Department> = {}
  for (const [code, dept] of Object.entries(raw.departments ?? {})) {
    departments[code] = dept ? normalizeDepartment(dept) : dept
  }
  const quarterLabel = raw.quarter_label ?? ''
  const quarter = raw.quarter ?? quarterFromLabel(quarterLabel)
  const isCurrentQuarter = raw.is_current_quarter ?? true
  return {
    ...(raw as Runway),
    quarter,
    is_current_quarter: isCurrentQuarter,
    budget: raw.budget ?? null,
    departments,
    department_order: raw.department_order ?? Object.keys(departments),
    unclassified: raw.unclassified ?? { count: 0, amount: 0, accounts: [] },
    budget_pin_release: raw.budget_pin_release ?? null,
    data_through: raw.data_through ?? null,
    period: raw.period ?? legacyPeriod(raw, quarter, isCurrentQuarter),
    // An API without tile modes shows every tile.
    tile_mode: raw.tile_mode ?? 'all',
    outside_tiles: raw.outside_tiles ?? { count: 0, actual: 0, budget: 0, tiles: [] },
  }
}

/**
 * The period of a quarter-only API (before ADR-0006): the quarter as of `as_of`, the current one
 * live and running, a past one complete.
 */
function legacyPeriod(raw: LooseRunway, quarter: string, isCurrent: boolean): RunwayPeriod {
  const m = /^(\d{4})-Q([1-4])$/.exec(quarter)
  const fromLabels = normalizeMonths((raw.quarter_months ?? []).map((label) => MONTH_ABBR.indexOf(label) + 1))
  const year = m ? Number(m[1]) : Number(/(\d{4})/.exec(raw.as_of ?? '')?.[1] ?? new Date().getFullYear())
  const months = m ? quarterMonths(Number(m[2])) : fromLabels.length ? fromLabels : quarterMonths(1)
  const end = periodEnd(year, months)
  const asOf = parseLongDate(raw.as_of ?? '') ?? (isCurrent ? businessToday() : end)
  const complete =
    !isCurrent || (raw.days_elapsed_in_quarter ?? 0) >= (raw.days_total_in_quarter ?? Number.POSITIVE_INFINITY)
  return {
    year,
    months,
    kind: months.length === 12 ? 'year' : wholeQuarter(months) ? 'quarter' : 'months',
    label: raw.quarter_label || periodLabel(year, months),
    start: periodStart(year, months),
    end,
    as_of: asOf,
    as_of_max: asOf,
    contiguous: isContiguous(months),
    complete,
    is_live: isCurrent,
    pace_applicable: isContiguous(months) && !complete,
  }
}

type LoosePeriods = Partial<Periods> & Pick<Periods, 'current' | 'quarters'>

/** A quarter-only API lists quarters: its years and months are derived from them. */
export function normalizePeriods(raw: LoosePeriods): Periods {
  const today = raw.today ?? businessToday()
  if (raw.years && raw.years.length > 0) return { ...raw, today, years: raw.years }
  const [ty, tm] = [Number(today.slice(0, 4)), Number(today.slice(5, 7))]
  const byYear = new Map<number, Map<number, { a: boolean; b: boolean }>>([[ty, new Map()]])
  for (const q of raw.quarters ?? []) {
    const m = /^(\d{4})-Q([1-4])$/.exec(q.quarter)
    if (!m) continue
    const year = Number(m[1])
    const months = byYear.get(year) ?? new Map<number, { a: boolean; b: boolean }>()
    for (const month of quarterMonths(Number(m[2]))) months.set(month, { a: q.has_actuals, b: q.has_budget })
    byYear.set(year, months)
  }
  const years = [...byYear.entries()]
    .filter(([year]) => year <= ty)
    .sort(([a], [b]) => b - a)
    .map(([year, months]) => ({
      year,
      months: MONTH_ABBR.map((label, i) => ({
        month: i + 1,
        label,
        has_actuals: months.get(i + 1)?.a ?? false,
        has_budget: months.get(i + 1)?.b ?? false,
        started: year < ty || i + 1 <= tm,
      })),
    }))
  return { current: raw.current, quarters: raw.quarters ?? [], today, years }
}

/** "Q3 2026" → "2026-Q3" ('' when the label has another shape). */
function quarterFromLabel(label: string): string {
  const m = /^Q([1-4])\s+(\d{4})$/.exec(label.trim())
  return m ? `${m[2]}-Q${m[1]}` : ''
}

/** Sage reports "active" / "inactive"; no status counts as active (fpa-backend's rule). */
export function isActiveStatus(status: string | null | undefined): boolean {
  return (status ?? 'active').trim().toLowerCase() !== 'inactive'
}

type LooseBudgetOption = Omit<BudgetOption, 'active'> & Partial<Pick<BudgetOption, 'active'>>

export function normalizeBudgetOptions(raw: LooseBudgetOption[]): BudgetOption[] {
  return (raw ?? []).map((o) => ({ ...o, active: o.active ?? isActiveStatus(o.status) }))
}

type LooseTile = Omit<TileSetting, 'inherited_depts'> & Partial<Pick<TileSetting, 'inherited_depts'>>
type LooseSageDepartment = Pick<SageDepartment, 'id' | 'title' | 'status' | 'tile'> &
  Partial<Pick<SageDepartment, 'parent_id' | 'counted_in' | 'via'>>

/** Older APIs know no Sage hierarchy: every department is top-level and counts where it is listed. */
export function normalizeDepartmentSettings(raw: {
  tile_mode?: DepartmentSettings['tile_mode']
  tiles?: LooseTile[]
  departments?: LooseSageDepartment[]
}): DepartmentSettings {
  return {
    tile_mode: raw.tile_mode ?? 'all',
    tiles: (raw.tiles ?? []).map((t) => ({ ...t, inherited_depts: t.inherited_depts ?? [] })),
    departments: (raw.departments ?? []).map((d) => {
      const via: DepartmentVia = d.via ?? (d.tile ? 'tile' : 'sage')
      return {
        ...d,
        parent_id: d.parent_id ?? null,
        counted_in: d.counted_in ?? d.tile ?? d.id,
        via,
      }
    }),
  }
}

type LooseAccountSetting = Omit<AccountSetting, 'counted_amount'> & Partial<Pick<AccountSetting, 'counted_amount'>>

/** Older APIs report only `quarter_amount` (every department). */
export function normalizeAccountSettings(raw: LooseAccountSetting[]): AccountSetting[] {
  return (raw ?? []).map((a) => ({ ...a, counted_amount: a.counted_amount ?? a.quarter_amount }))
}
